/* eslint-disable react/display-name */
import { openModal } from "action/modal/openModal";
import classNames from "classnames";
import useScreenSize from "hoc/useScreenSize";
import { RootState } from "interfaces/generics.type";
import dynamic from "next/dynamic";
import Head from "next/head";
import {
  forwardRef, useEffect, useRef, useState,
} from "react";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import HideIf from "util/HideIf";
import { getProductImage } from "util/helper/getProductImage";
import { getRelativeImagePath } from "util/helper/getRelativeImagePath";
import { getThumbImage } from "util/helper/getThumbImage";
import { isApparelProduct } from "util/product/apparel";
import { getProductSeason } from "util/product/get-product-season";
import { getImageAlt } from "util/product/getImageAlt";
import { PAGE_TYPES } from "util/product/product-constants";
import WarrantyReturnPolicyDesktop from "../warranty-return-policy/WarrantyReturnPolicyDesktop";
import MobileProductImage from "./MobileProductImage";
import { NextA, PrevA } from "./StyleComponent";
import { getSpinningImageIdx } from "./image-helper";
import styles from "./mobile-image-slider.module.scss";
import ThumbnailList from "./thumbnail/ThumbnailList";
const SliderMainImageV2 = dynamic(() => import(/* webpackChunkName: "slider-main-image-v2" */ "./SliderMainImageV2"),
  { loading: () => <Skeleton height={450} count={1} /> });
const ForwardedRefSliderMainImageV2 = forwardRef((props, ref) => (
  <SliderMainImageV2 {...props} forwardedRef={ref} />
));

const maxThumbnailInViewport = 5;

const desktopDim = {
  imgContainerheight: 400,
  imgWidth: 552,
  imgHeight: 400,
  thumbnailContainer: 80,
  thumbnailImage: 60,
};

const tabletDim = {
  imgContainerheight: 400,
  imgWidth: 350,
  imgHeight: 300,
  thumbnailContainer: 80,
  thumbnailImage: 60,
};

const mobileDim = {
  imgContainerheight: 300,
  imgWidth: 300,
  imgHeight: 300,
  thumbnailContainer: 70,
  thumbnailImage: 70,
};

function getDimesions(isDesktop: any, isTablet: any, isMobile: any) {
  if (isDesktop) {
    return desktopDim;
  }
  if (isTablet) {
    return tabletDim;
  }
  if (isMobile) {
    return mobileDim;
  }
  return desktopDim;
}

/**
 * 0 Image - rugged-ridge-tire-carrier-11585-01
 * 1 imgae - skf-front-output-shaft-seal-18752
 * 2 Image - national-oil-seals-front-output-shaft-seal-710746
 * 3 Image - moog-universal-joint-270
 * 4 Image -
 * 5 Image - raybestos-front-disc-brake-rotor-96415r
 * 6 Image - dorman-help-exterior-door-handle-80545
 *
 * Out of stock - /bosch-remanufactured-alternator-al1299x
 */
let slideIndex = 0;
let changedViaThumNail = false;
function MobileImageSlider(props: any) {
  const {
    productImages = [],
    productImagesV2 = [],
    imageTitle,
    selectedImage,
    setSelectedImage,
    isPopUp,
    getThumbImagePath = getThumbImage,
    getProductImagePath = getProductImage,
    isFluidWidth,
    outOfStock = false,
    zoomImagePosition,
    showDots,
    marketingImages,
    isHovered,
    fromSideMenu,
    product,
    productLineVideos = [],
    zoomOpen,
    videos = [],
    largeImage,
    isRegularDesktop = false,
  } = props;
  const mainSliderRef = useRef(null);
  const mainSliderRefV2 = useRef(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isMobile, isTablet, isDesktop] = useScreenSize();
  const { currentPageType } = useSelector((store: RootState) => store.utils);
  const dispatch = useDispatch();
  const { businessCustomer } = useSelector((state: RootState) => state.authenticationState);
  const isApparel = isApparelProduct(product);
  const season = getProductSeason(product);

  const isWebpImage = productImagesV2.length > 0;
  const productImagesArr = isWebpImage ? productImagesV2 : productImages;
  const [spinningImage = { urls: [] }] = productImagesArr.filter((_: any) => _.type === "SPINNING_IMAGE") || [];

  const productImageUrls = (productImagesArr.find((_: any) => _.type === "SINGLE_IMAGE")?.urls || []).filter((_: any) => _ !== "");

  let thumbnails = productImageUrls.map((image: string) => ({ type: "image", image: getThumbImagePath(image, isWebpImage) }));

  const spinningThumbnail = spinningImage.urls.length > 0
    ? {
      type: "spinning",
      image: getThumbImagePath(spinningImage.urls[0], isWebpImage),
    }
    : null;

  const updatedVideos = (productLineVideos && productLineVideos.length > 0 ? productLineVideos : videos).map((vid: any) => ({ ...vid, type: "video" }));

  const [showSpinningImage, setShowSpinningImage] = useState(spinningImage.urls.length > 0);

  if (spinningThumbnail) {
    const spinningImageIdx = getSpinningImageIdx(isDesktop, thumbnails.length, updatedVideos.length);
    thumbnails = [...thumbnails.slice(0, spinningImageIdx), spinningThumbnail, ...thumbnails.slice(spinningImageIdx)];
  }
  if (updatedVideos.length > 0 && !isDesktop) {
    thumbnails = [...thumbnails.slice(0, 3), ...updatedVideos, ...thumbnails.slice(3)];
  }

  const showSliderThum = (!isDesktop && !isPopUp) && (thumbnails.length > maxThumbnailInViewport || (isMobile && thumbnails.length > 4));

  const dim = getDimesions(isDesktop, isTablet, isMobile);

  const [imageError, setImageError] = useState(false);

  function plusSlides(n: any) {
    calculateCurrentIndex((slideIndex += n));
  }

  function calculateCurrentIndex(n: any) {
    if (n >= thumbnails.length) {
      slideIndex = 0;
    }
    if (n < 0) {
      slideIndex = thumbnails.length - 1;
    }
    setSelectedImage(slideIndex);
  }

  function changeMainImage(index: any) {
    changedViaThumNail = true;
    if (index === -1) {
      setSelectedImage(-1);
      setShowSpinningImage(true);
      return;
      // spinning image
    }
    setShowSpinningImage(false);
    if (selectedImage === index) {
      return;
    }
    try {
      // 2 way binding, for both slider.
      mainSliderRefV2.current.setImage(index);
    } catch (err) {
      // https://partsavatar.sentry.io/issues/4399237489/?project=1187556&query=is%3Aunresolved&referrer=issue-stream&sort=freq&statsPeriod=30d&stream_index=11
      console.log(err);
    }
    setSelectedImage(index);
  }

  function handleImageClick(index = null) {
    if (!product && currentPageType !== PAGE_TYPES.PRODUCT_LINE_PAGE) {
      return;
    }
    dispatch(openModal("thumbnailVideo", {
      productImagesV2,
      productImages,
      getThumbImagePath,
      marketingImages,
      imageTitle,
      product,
      productLineVideos: updatedVideos,
      isImageClick: true,
      defaultSelectedImage: index || selectedImage,
      isMobile,
    }));
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Because it doesn't mount properly & changes the index using slickGoTo.
    // only happening when we open zoom image first time after selecting 2nd or other image
    // from slick slider on mobile.
    if (isMounted && mainSliderRef.current && mainSliderRef.current.innerSlider) {
      mainSliderRef.current.innerSlider.slickGoTo(selectedImage);
    }
  }, [selectedImage, isMounted]);

  function handleDotClick(imageIndex: number) {
    setCurrentImageIndex(imageIndex);
    setSelectedImage(imageIndex);
  }

  const imagePath = isWebpImage ? productImagesV2[0]?.urls?.[0] : productImages[0]?.urls?.[0];
  let imagePreload = "";
  if (isWebpImage) {
    if (isMobile) {
      imagePreload = "m";
    } else if (isTablet) {
      imagePreload = "t";
    } else if (isDesktop) {
      imagePreload = "ds";
    }
  }
  const firstImage = imagePath ? getProductImagePath(imagePath, false, imagePreload) : "";

  return (
    <>
      {firstImage && (
        <Head>
          <link
            rel="preload"
            as="image"
            fetchPriority="high"
            href={firstImage}
          />
        </Head>
      )}

      <HideIf show={!isRegularDesktop}>
        <MobileProductImage {...{
          selectedImage,
          productImages,
          productImagesV2,
          dim,
          imageTitle,
          isPopUp,
          getProductImagePath,
          spinningImage,
          isHovered,
          updatedVideos,
          marketingImages,
          isFluidWidth,
          setCurrentImageIndex,
          maxThumbnailInViewport,
          setSelectedImage,
          zoomImagePosition,
          fromSideMenu,
          imagePreload,
          isWebpImage,
          handleImageClick,
          largeImage,
          getThumbImagePath,
          product,
        }}
        />
      </HideIf>

      <HideIf show={isRegularDesktop}>
        <div className={styles.main_image_div} id="divImageContainer">

          <div className={styles.desktop_image_wrapper}>

            {/* <FinalSaleTag product={product} className={styles.final_sale_tag} /> */}

            <HideIf show={thumbnails.length > 0 || showSpinningImage}>

              {/* https://partsavatar.ca/walker-products-idle-air-control-motor-215-91001 */}
              {/* <NoSsr> */}
              <ForwardedRefSliderMainImageV2
                ref={mainSliderRefV2}
                {...{
                  selectedImage,
                  productImages,
                  productImagesV2,
                  dim,
                  imageTitle,
                  isPopUp,
                  getProductImagePath,
                  spinningImage,
                  isHovered,
                  videosCount: updatedVideos.length,
                }}
                marketingImages={marketingImages}
                isFluidWidth={isFluidWidth}
                isDesktop={isDesktop}
                onArrowClick={(index) => {
                  changedViaThumNail = false;
                  setCurrentImageIndex(maxThumbnailInViewport * Math.trunc(index / maxThumbnailInViewport));
                  if (!changedViaThumNail) {
                    setSelectedImage(index);
                  } else if (selectedImage !== index) {
                    if (
                      mainSliderRefV2.current
                    && mainSliderRefV2.current.innerSlider
                    ) {
                      mainSliderRef.current.innerSlider.slickGoTo(selectedImage);
                    }
                  }
                }}
                afterChange={(index: any) => {
                  if (!changedViaThumNail) {
                    setSelectedImage(index);
                  } else if (selectedImage !== index) {
                    if (
                      mainSliderRefV2.current
                    && mainSliderRefV2.current.innerSlider
                    ) {
                      mainSliderRefV2.current.innerSlider.slickGoTo(selectedImage);
                    }
                  }
                }}
                zoomImagePosition={zoomImagePosition}
                showDots={showDots}
                fromSideMenu={fromSideMenu}
                imageSize={imagePreload}
                isWebpImage={isWebpImage}
                handleDotClick={handleDotClick}
                handleImageClick={handleImageClick}
                largeImage={largeImage}
                showArrows={Boolean(isPopUp)}
              />
              {/* </NoSsr> */}
            </HideIf>
            {/* For SSR and CSR when images are 0 or 1 */}

            <HideIf show={thumbnails.length === 0 && !showSpinningImage}>

              <div className={styles.thumbnail_wrapper}>

                <HideIf show={outOfStock}>

                  <div className={styles.watermark}>

                    <h1><strong>SOLD OUT</strong></h1>
                  </div>
                </HideIf>
                <img
                  className="center-image"
                  alt={imageError ? "Image not found" : getImageAlt(thumbnails[selectedImage], imageTitle)}
                  style={{
                    maxWidth: dim.imgWidth,
                    maxHeight: dim.imgHeight,
                  }}
                  src={thumbnails[selectedImage] || getRelativeImagePath("/images/common/image-not-available_16051144.png")}
                  onError={() => setImageError(true)}
                />
              </div>
              {/* show arrow when image are more then 1 */}

              <HideIf show={thumbnails.length > 1}>

                <PrevA onClick={() => plusSlides(-1)}>&#10094;</PrevA>

                <NextA onClick={() => plusSlides(1)}>&#10095;</NextA>
              </HideIf>
            </HideIf>
          </div>

          <HideIf show={!businessCustomer && !isApparel && isDesktop}>
            <div className="gallery-links text-center col-lg-12 col-md-12 col-sm-12 col-xs-12 m-t-20">
              <WarrantyReturnPolicyDesktop
                season={season}
              />
            </div>
          </HideIf>
        </div>
      </HideIf>

      <HideIf show={isRegularDesktop && ((!showDots) || zoomOpen)}>
        <div className={classNames(styles.thumbnail_slider_div, {
          [styles.desktop_thumbnail_slider]: !isPopUp,
        })}
        >
          <HideIf show={!showSliderThum}>
            <div
              className={styles.gallery}
            >
              <ThumbnailList
                {...{
                  thumbnails,
                  dim,
                  isDesktop,
                  showSliderThum,
                  imageTitle,
                  isMobile,
                  selectedImage,
                  changeMainImage,
                  spinningImage,
                  getThumbImagePath,
                  isWebpImage,
                  productImages,
                  productImagesV2,
                  marketingImages,
                  product,
                  productLineVideos,
                  videos,
                  isPopUp,
                }}
              />
            </div>
          </HideIf>
        </div>
      </HideIf>
    </>
  );
}

export default MobileImageSlider;
